"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useMemo, useState } from "react"
import { ChevronRight, FolderTree, Loader2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  useCatalogFilterOptions,
  useCatalogProducts,
  type CatalogProduct,
} from "./use-catalog-products"
import { CatalogStatusFilter } from "./catalog-status-filter"
import { useTranslations } from "next-intl"


interface CatalogCategoriesTabProps {
  integrationId: string
}

const UNCATEGORIZED_KEY = "__uncategorized__"
const UNCATEGORIZED_LABEL = "Uncategorized"

/**
 * Products grouped by Shopify taxonomy category (`shopify_products.taxonomy_category_name`).
 * Uses `all=true` against the catalog endpoint — the route caps the result at
 * 10 000 rows which covers nearly every tenant; if anyone exceeds that we can
 * switch to paginated load-per-category, but the additional complexity isn't
 * worth it before there's a real shop hitting the ceiling.
 */
export function CatalogCategoriesTab({ integrationId }: CatalogCategoriesTabProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("Shopify.ShopifyCatalogCatalogCategoriesTab")

  // Multi-select status filter — consistent with Overview / COGS tabs. Default
  // to ACTIVE so the categories list reflects the live catalog by default.
  const [statuses, setStatuses] = useState<string[]>(["ACTIVE"])
  const { options: filterOptions } = useCatalogFilterOptions(integrationId)

  const { products, total, isLoading, error: catalogError } = useCatalogProducts({
    integrationId,
    all: true,
    statuses,
  })

  const groups = useMemo(() => groupByCategory(products), [products])

  if (catalogError) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <CatalogStatusFilter
            availableStatuses={filterOptions.statuses}
            selectedStatuses={statuses}
            onChange={setStatuses}
          />
        </div>
        <Card>
          <CardContent className="py-16 flex flex-col items-center text-center gap-3">
            <FolderTree className="h-10 w-10 text-destructive/60" />
            <div className="max-w-md text-sm text-destructive">{catalogError}</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading && products.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 flex items-center justify-center text-muted-foreground gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span><span key={tUi("loadingCategories")}>{tUi("loadingCategories")}</span></span>
        </CardContent>
      </Card>
    )
  }

  if (groups.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <CatalogStatusFilter
            availableStatuses={filterOptions.statuses}
            selectedStatuses={statuses}
            onChange={setStatuses}
          />
        </div>
        <Card>
          <CardContent className="py-16 flex flex-col items-center text-center text-muted-foreground gap-3">
            <FolderTree className="h-10 w-10 text-muted-foreground/60" />
            <div key={statuses.length > 0
                ? tUi("noProductsMatchTheCurrentStatusFilter")
                : tUi("categoriesWillAppearAfterAShopifyCatalogSync")} className="max-w-md text-sm">
              {statuses.length > 0
                ? tUi("noProductsMatchTheCurrentStatusFilter")
                : tUi("categoriesWillAppearAfterAShopifyCatalogSync")}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <CatalogStatusFilter
          availableStatuses={filterOptions.statuses}
          selectedStatuses={statuses}
          onChange={setStatuses}
        />
        <div className="text-sm text-muted-foreground ml-auto">
          {groups.length.toLocaleString(uiFormattingLocale)} <span><span key={tUi("categories")}>{tUi("categories")}</span></span> {total.toLocaleString(uiFormattingLocale)} <span><span key={tUi("products")}>{tUi("products")}</span></span>
        </div>
      </div>
      <Card>
        <CardContent className="p-0 divide-y">
          {groups.map((g) => (
            // Plain <details> avoids pulling in a Radix accordion just for
            // open/close state. Accessibility & keyboard behaviour come for
            // free; styling is open:rotate for the chevron via the [open:]
            // sibling selector.
            <details key={g.key} className="group px-4 py-3">
              <summary className="flex items-center gap-3 cursor-pointer list-none">
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
                <span className="font-medium">{g.label}</span>
                <Badge variant="secondary" className="ml-auto">
                  {g.products.length}
                </Badge>
              </summary>
              <ul className="mt-2 ml-7 space-y-1">
                {g.products.map((p) => (
                  <li key={p.product_id} className="py-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex-1 truncate">{p.title}</span>
                      {p.vendor && (
                        <span className="text-xs text-muted-foreground shrink-0">{p.vendor}</span>
                      )}
                    </div>
                    {p.sku && (
                      <div className="text-xs text-muted-foreground truncate">
                        <span><span key={"SKU:"}>{"SKU:"}</span></span> {p.sku}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

interface CategoryGroup {
  key: string
  label: string
  products: CatalogProduct[]
}

/**
 * `??` only coalesces null/undefined. Shopify occasionally stores empty-string
 * `taxonomy_category_name` for products that have a category id but no
 * localized name (e.g. custom taxonomies, or rows synced before localization
 * was published). Without trimming, those products bucket under a label of
 * `""` which sorts as "less than" anything and surfaces as the leading
 * untitled accordion. Trim + fallback to the id keeps such categories visible
 * AND distinct from the catch-all "Uncategorized" bucket.
 */
function nonEmpty(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function groupByCategory(products: ReadonlyArray<CatalogProduct>): CategoryGroup[] {
  const buckets = new Map<string, CategoryGroup>()
  for (const p of products) {
    const name = nonEmpty(p.taxonomy_category_name)
    const id = nonEmpty(p.taxonomy_category_id)
    const type = nonEmpty(p.product_type)

    let label: string
    let key: string
    if (name) {
      label = name
      key = id ?? `__name__:${name}`
    } else if (id) {
      // Have a Shopify category id but no localized name — show the id as the
      // label so the category stays addressable. Mirrors the RPC fallback in
      // `get_products_catalog_filter_options` (`COALESCE(name, id)`).
      label = id
      key = id
    } else if (type) {
      label = type
      key = `__type__:${type}`
    } else {
      label = UNCATEGORIZED_LABEL
      key = UNCATEGORIZED_KEY
    }

    const existing = buckets.get(key)
    if (existing) {
      existing.products.push(p)
    } else {
      buckets.set(key, { key, label, products: [p] })
    }
  }
  // Sort categories alphabetically; uncategorized always last.
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.key === UNCATEGORIZED_KEY) return 1
    if (b.key === UNCATEGORIZED_KEY) return -1
    return a.label.localeCompare(b.label)
  })
}
